import * as React from "react"
import { useCallback, useEffect, useState, useMemo } from "react"
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Grid,
    Modal
} from "@mui/material"
import { IconSeeker, ImageSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS , PRODUCT_MATCH_TYPES} from "@constants"
import { batchItemEditDrawer } from "../@signals"
import SkuOption from "../../../ProductsReview/EditMappingDrawer/SkuOption"
import ProductOption from "./ProductOption"


// ─── Image Comparison Modal (embedded) ───────────────────────────────────────

function ImageContainer({ url, label, labelColor = 'default' }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                bgcolor: 'background.default',
                position: 'relative'
            }}
        >
            <Box position="absolute" top={8} left={8} zIndex={1}>
                {!!label && <Chip size="small" label={label} color={labelColor} sx={{ height: 22 }} />}
            </Box>

            <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                '&:hover .zoomable': { transform: 'scale(1.5)' }
            }}>
                {url ? (
                    <ImageSeeker
                        useSpinner
                        lazyLoad={false}
                        src={url}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            transition: 'transform .2s ease',
                            willChange: 'transform',
                            cursor: 'zoom-in',
                        }}
                        className="zoomable"
                    />
                ) : (
                    <Box sx={{ color: '#9ca3af', textAlign: 'center' }}>
                        <IconSeeker icon="image" sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body2">No image</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

function ImageComparisonModal({ data, onClose }) {
    const isOpen = data !== null

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
        >
            <Box
                sx={{
                    width: '90vw',
                    maxWidth: 1200,
                    height: '80vh',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <Box sx={{
                    p: 2,
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Image Comparison
                    </Typography>
                    <IconButton onClick={onClose}>
                        <IconSeeker icon="xmark" />
                    </IconButton>
                </Box>

                {/* Product info */}
                {(data?.sourceName || data?.candidateName) && (
                    <Box sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>
                            {data?.sourceName || '-'}
                        </Typography>
                        {data?.candidateName && (
                            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, textAlign: 'right', color: '#059669' }} noWrap>
                                {data.candidateName}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Images */}
                <Box sx={{ flex: 1, p: 2, display: 'flex', gap: 2, overflow: 'hidden' }}>
                    <ImageContainer
                        url={data?.sourceImage}
                        label={data?.sourceLabel || 'Source'}
                        labelColor="warning"
                    />
                    <ImageContainer
                        url={data?.candidateImage}
                        label={data?.candidateLabel || 'Candidate'}
                        labelColor="success"
                    />
                </Box>
            </Box>
        </Modal>
    )
}


// ─── Main Drawer ─────────────────────────────────────────────────────────────

export default function EditMappingDrawer({ fetchItems }) {
    const { showToast } = useAppToast()
    const drawerData = batchItemEditDrawer.value
    const isOpen = drawerData !== null

    const item = drawerData?.item
    const batchType = drawerData?.batchType || 'sku2raw'
    const isRaw2Sku = batchType === 'raw2sku'
    // Both product↔product directions share PRODUCTS_TO_BE_MATCHED and this layout;
    // only which side anchored the batch differs.
    const isRaw2Raw = PRODUCT_MATCH_TYPES.includes(batchType)

    const [allOptions, setAllOptions] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [selectedId, setSelectedId] = useState(null)
    const [visibleCount, setVisibleCount] = useState(30)
    const [imageModalData, setImageModalData] = useState(null)

    const brandId = item?.idBrand
    // raw2raw candidates live at the COMPETITOR (target) site
    const siteId = isRaw2Raw ? item?.idSiteTarget : item?.idSite
    const mappingId = item?.idMapping

    // Determine source & current mapping info based on batch type
    const sourceInfo = useMemo(() => {
        if (!item) return {}
        if (isRaw2Raw) {
            return {
                name: item.own?.name,
                image: item.own?.image,
                url: item.own?.url,
                label: 'Own Product',
                id: item.own?.productId,
                idLabel: 'Product ID'
            }
        }
        if (isRaw2Sku) {
            return {
                name: item.productName,
                image: item.productImage,
                url: item.productUrl,
                label: 'Raw Product',
                id: item.productId,
                idLabel: 'Product ID'
            }
        }
        return {
            name: item.skuName,
            image: item.skuImage,
            label: 'SKU',
            id: item.idSku,
            idLabel: 'SKU ID'
        }
    }, [item, isRaw2Sku, isRaw2Raw])

    const currentMapping = useMemo(() => {
        if (!item) return null
        if (isRaw2Raw) {
            if (!item.competitor) return null
            return {
                name: item.competitor.name,
                image: item.competitor.image,
                url: item.competitor.url,
                id: item.competitor.productId,
                idLabel: 'Product ID'
            }
        }
        if (isRaw2Sku) {
            // Current mapped SKU
            if (!item.idSkuMapped) return null
            return {
                name: item.skuName,
                image: item.skuImage,
                id: item.idSkuMapped,
                idLabel: 'SKU ID'
            }
        }
        // Current mapped product
        if (!item.productIdMapped) return null
        return {
            name: item.productName,
            image: item.productImage,
            url: item.productUrl,
            id: item.productIdMapped,
            idLabel: 'Product ID'
        }
    }, [item, isRaw2Sku, isRaw2Raw])

    // Filter options locally (AND with comma)
    const filteredOptions = useMemo(() => {
        if (!searchInput.trim()) return allOptions

        const searchTerms = searchInput.toLowerCase().split(',').map(t => t.trim()).filter(Boolean)
        if (searchTerms.length === 0) return allOptions

        if (isRaw2Sku) {
            // Filter SKUs
            const matchesTerm = (sku, term) =>
                (sku.skuName && sku.skuName.toLowerCase().includes(term)) ||
                (sku.ean && sku.ean.toLowerCase().includes(term)) ||
                (sku.reference && sku.reference.toLowerCase().includes(term)) ||
                (sku.skuID && String(sku.skuID).includes(term))

            return allOptions.filter(sku => searchTerms.every(term => matchesTerm(sku, term)))
        }

        // Filter products
        const matchesTerm = (p, term) =>
            (p.productTitle && p.productTitle.toLowerCase().includes(term)) ||
            (p.variant && p.variant.toLowerCase().includes(term)) ||
            (p.productID && String(p.productID).toLowerCase().includes(term))

        return allOptions.filter(p => searchTerms.every(term => matchesTerm(p, term)))
    }, [allOptions, searchInput, isRaw2Sku])

    const visibleOptions = useMemo(() => {
        return filteredOptions.slice(0, visibleCount)
    }, [filteredOptions, visibleCount])

    const hasMoreToShow = filteredOptions.length > visibleCount

    // Load candidates when drawer opens
    const loadCandidates = useCallback(() => {
        if (!brandId) return

        setIsLoading(true)

        const url = isRaw2Sku
            ? `${BACKEND_APIS.MATCHERS}/selectors/skus-by-brand/${brandId}`
            : isRaw2Raw
                ? `${BACKEND_APIS.MATCHERS}/products/candidates/${brandId}/${siteId}`
                : `${BACKEND_APIS.MATCHERS}/selectors/sku-mapping/${brandId}/${siteId}`

        postSubmit(url, {})
            .then(res => setAllOptions(res?.options || []))
            .catch(() => {
                showToast.error(`Error loading ${isRaw2Sku ? 'SKUs' : 'products'}`)
                setAllOptions([])
            })
            .finally(() => setIsLoading(false))
    }, [brandId, siteId, isRaw2Sku, isRaw2Raw])

    useEffect(() => {
        if (isOpen && brandId) loadCandidates()
    }, [isOpen, brandId])

    // Reset state when drawer closes
    useEffect(() => {
        if (!isOpen) {
            setAllOptions([])
            setSearchInput('')
            setSelectedId(null)
            setVisibleCount(30)
            setImageModalData(null)
        }
    }, [isOpen])

    useEffect(() => { setVisibleCount(30) }, [searchInput])

    const handleClose = () => { batchItemEditDrawer.value = null }

    const handleImageClick = (candidate) => {
        if (isRaw2Raw) {
            setImageModalData({
                sourceName: item?.own?.name,
                sourceImage: item?.own?.image,
                sourceLabel: 'Own Product',
                candidateName: candidate.productTitle,
                candidateImage: candidate.thumbnail,
                candidateLabel: 'Competitor'
            })
            return
        }
        if (isRaw2Sku) {
            setImageModalData({
                sourceName: item?.productName,
                sourceImage: item?.productImage,
                sourceLabel: 'Raw Product',
                candidateName: candidate.skuName,
                candidateImage: `${BACKEND_APIS.BACKEND}/sku/image/${candidate.skuID}`,
                candidateLabel: 'SKU'
            })
        } else {
            setImageModalData({
                sourceName: item?.skuName,
                sourceImage: item?.skuImage,
                sourceLabel: 'SKU',
                candidateName: candidate.productTitle,
                candidateImage: candidate.thumbnail,
                candidateLabel: 'Product'
            })
        }
    }

    const handleSave = async () => {
        if (isSaving || !selectedId) return

        setIsSaving(true)
        try {
            if (isRaw2Raw) {
                // raw2raw: validate a competitor for the own product (the writer
                // unmaps the previously materialized member automatically)
                await postSubmit(`${BACKEND_APIS.MATCHERS}/products/mapping-product`, {
                    mappingID: mappingId,
                    productID: selectedId,
                    group: true
                })
            } else if (isRaw2Sku) {
                // raw2sku: save SKU mapping for a product
                await postSubmit(`${BACKEND_APIS.MATCHERS}/raw/mapping-product`, {
                    skuID: selectedId,
                    mappingID: mappingId,
                    siteID: siteId,
                    productID: item?.productId,
                    brandID: brandId,
                    notFound: false,
                    qa: true
                })
            } else {
                // sku2raw: save product mapping for a SKU
                await postSubmit(`${BACKEND_APIS.MATCHERS}/reverse/mapping-product`, {
                    productID: selectedId,
                    mappingID: mappingId,
                    siteID: siteId,
                    skuID: item?.idSku,
                    brandID: brandId,
                    productURL: "",
                    notFound: false,
                    qa: true
                })
            }

            showToast.success('Mapping updated successfully')
            handleClose()
            if (fetchItems) fetchItems()
        } catch (error) {
            showToast.error('Error updating mapping')
        } finally {
            setIsSaving(false)
        }
    }

    const handleNotFound = async () => {
        setIsSaving(true)
        try {
            if (isRaw2Raw) {
                // raw2raw not-found: the writer unmaps the materialized member and
                // flags the row as not_found
                await postSubmit(`${BACKEND_APIS.MATCHERS}/products/mapping-product`, {
                    mappingID: mappingId,
                    productID: null
                })
            } else if (isRaw2Sku) {
                await postSubmit(`${BACKEND_APIS.MATCHERS}/raw/mapping-product`, {
                    skuID: null,
                    mappingID: mappingId,
                    siteID: siteId,
                    productID: item?.productId,
                    brandID: brandId,
                    notFound: true,
                    qa: true
                })
            } else {
                await postSubmit(`${BACKEND_APIS.MATCHERS}/reverse/mapping-product`, {
                    productID: null,
                    mappingID: mappingId,
                    siteID: siteId,
                    skuID: item?.idSku,
                    brandID: brandId,
                    productURL: "",
                    notFound: true,
                    qa: true
                })
            }

            showToast.success('Marked as Not Found')
            handleClose()
            if (fetchItems) fetchItems()
        } catch (error) {
            showToast.error('Error marking as Not Found')
        } finally {
            setIsSaving(false)
        }
    }

    const handleRemoveMapping = async () => {
        setIsSaving(true)
        try {
            if (isRaw2Raw) {
                // raw2raw remove: unmap the materialized member and send the row
                // back to PENDING (it re-enters the QA pool)
                await postSubmit(`${BACKEND_APIS.MATCHERS}/products/mapping-product`, {
                    mappingID: mappingId,
                    productID: null,
                    removeMapping: true
                })
            } else if (isRaw2Sku) {
                await postSubmit(`${BACKEND_APIS.MATCHERS}/raw/mapping-product`, {
                    skuID: null,
                    mappingID: mappingId,
                    siteID: siteId,
                    productID: item?.productId,
                    brandID: brandId,
                    notFound: false,
                    qa: true
                })
            } else {
                await postSubmit(`${BACKEND_APIS.MATCHERS}/reverse/mapping-product`, {
                    productID: null,
                    mappingID: mappingId,
                    siteID: siteId,
                    skuID: item?.idSku,
                    brandID: brandId,
                    productURL: "",
                    notFound: false,
                    qa: true
                })
            }

            showToast.success('Mapping removed')
            handleClose()
            if (fetchItems) fetchItems()
        } catch (error) {
            showToast.error('Error removing mapping')
        } finally {
            setIsSaving(false)
        }
    }

    const candidateLabel = isRaw2Sku ? 'SKUs' : 'products'

    return (
        <>
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={handleClose}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 600, md: 700 } }
                }}
            >
                {isOpen && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Header */}
                        <Box sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Edit Mapping
                                <Chip label={batchType} size="small" sx={{ ml: 1 }} variant="outlined" />
                            </Typography>
                            <IconButton onClick={handleClose}>
                                <IconSeeker icon="xmark" />
                            </IconButton>
                        </Box>

                        {/* Source Info */}
                        <Box sx={{ p: 2, backgroundColor: '#fef3c7', borderBottom: '1px solid #e5e7eb' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#92400e' }}>
                                {sourceInfo.label}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <ImageSeeker
                                    src={sourceInfo.image}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        borderRadius: 1,
                                        border: '1px solid #e5e7eb',
                                        objectFit: 'contain',
                                        backgroundColor: '#fff'
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {sourceInfo.name || '-'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                            {sourceInfo.idLabel}: {sourceInfo.id}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Site: {item?.siteName} | Brand: {item?.brandName}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Current Mapping */}
                        {currentMapping && (
                            <Box sx={{ p: 2, backgroundColor: '#d1fae5', borderBottom: '1px solid #e5e7eb' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#065f46' }}>
                                        Current Mapping
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<IconSeeker icon="link-slash" />}
                                        onClick={handleRemoveMapping}
                                        disabled={isSaving}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Avatar
                                        src={currentMapping.image}
                                        variant="rounded"
                                        sx={{ width: 50, height: 50, bgcolor: '#ecfdf5' }}
                                    />
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                            {currentMapping.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {currentMapping.idLabel}: {currentMapping.id}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Search */}
                        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={`Filter ${candidateLabel}... (use comma for AND: text1,text2)`}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconSeeker icon="search" sx={{ color: '#9ca3af' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchInput && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchInput('')}>
                                                <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                helperText={!isLoading && allOptions.length > 0 ?
                                    `Showing ${visibleOptions.length} of ${filteredOptions.length} ${candidateLabel}${filteredOptions.length !== allOptions.length ? ` (${allOptions.length} total)` : ''}` :
                                    undefined
                                }
                            />
                        </Box>

                        {/* Candidates List */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : !brandId ? (
                                <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                                    <IconSeeker icon="circle-exclamation" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography>No brand information available</Typography>
                                </Box>
                            ) : filteredOptions.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                                    <IconSeeker icon="box-open" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography>
                                        {searchInput ? `No ${candidateLabel} match your filter` : `No ${candidateLabel} found for this brand`}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        {visibleOptions.map((option) => {
                                            const optionId = isRaw2Sku ? option.skuID : option.productID
                                            return (
                                                <Grid item xs={12} key={optionId}>
                                                    {isRaw2Sku ? (
                                                        <SkuOption
                                                            sku={option}
                                                            isSelected={selectedId === optionId}
                                                            isSaving={isSaving}
                                                            onSelect={() => setSelectedId(optionId)}
                                                            onImageClick={() => handleImageClick(option)}
                                                        />
                                                    ) : (
                                                        <ProductOption
                                                            product={option}
                                                            isSelected={selectedId === optionId}
                                                            isSaving={isSaving}
                                                            onSelect={() => setSelectedId(optionId)}
                                                            onImageClick={() => handleImageClick(option)}
                                                        />
                                                    )}
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                    {hasMoreToShow && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setVisibleCount(prev => prev + 30)}
                                                startIcon={<IconSeeker icon="chevron-down" />}
                                            >
                                                Load more ({filteredOptions.length - visibleCount} remaining)
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* Footer */}
                        <Box sx={{
                            p: 2,
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                            alignItems: 'center'
                        }}>
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleNotFound}
                                disabled={isSaving}
                                startIcon={isSaving ? <CircularProgress size={16} /> : <IconSeeker icon="ban" />}
                            >
                                Not Found
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={isSaving || !selectedId}
                                startIcon={isSaving ? <CircularProgress size={16} /> : <IconSeeker icon="check" />}
                            >
                                {isSaving ? 'Saving...' : 'Accept'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Drawer>

            <ImageComparisonModal
                data={imageModalData}
                onClose={() => setImageModalData(null)}
            />
        </>
    )
}
